// Admin Audit API layer — typed wrapper over the LIVE /v3/admin/audit-logs contract.
//
// ⚠️ LIVE JSON IS GROUND TRUTH (FACTS PACK §1, probe-confirmed against v3-api-dev,
// commit a33e3b5): the deployed build returns { data.logs[], data.next_cursor }
// (NOT the bare array the source checkout's audit.go would produce). Rows may also
// carry optional reason / case_id (the device.disable row had reason="recon" live),
// so this layer surfaces them. All timestamps are MICROSECONDS since epoch.
//
// TARGETED query: exactly ONE of target_id | actor_user_id | case_id, else the server
// returns 400 INVALID_REQUEST ("exactly one of ... required"). The UI enforces this
// pre-flight so the 400 should never fire, but the error is mapped just in case.

import { apiFetch, normalizeCursor } from '../../lib/api';

/** The single required query axis. target_id is PREFIXED live: "device:..." / "user:..." */
export type AuditAxis = 'target_id' | 'actor_user_id' | 'case_id';

export interface AuditLog {
  action: string;
  target_id?: string;
  target_type?: string; // 'user' | 'device' | 'audit'
  actor_user_id?: string;
  actor_role?: string; // 'admin' | 'owner'
  reason?: string;
  case_id?: string;
  ts_us?: number; // µs
}

export interface AuditQuery {
  axis: AuditAxis;
  /** the axis value (e.g. "device:device-e2e-001", a user id, or a case id) */
  value: string;
  /** optional extra filters */
  action?: string;
  fromUs?: number;
  toUs?: number;
  limit?: number;
  cursor?: string;
}

export interface AuditPage {
  logs: AuditLog[];
  /** null = no more pages (covers "" and an omitted next_cursor). */
  nextCursor: string | null;
}

/**
 * GET /v3/admin/audit-logs?<axis>=<value>&... — TARGETED. Returns { data.logs[],
 * data.next_cursor }. Self-audited server-side (a `audit.query` row is written).
 */
export async function queryAuditLogs(q: AuditQuery): Promise<AuditPage> {
  const query: Record<string, string | number | undefined> = {
    [q.axis]: q.value,
    action: q.action || undefined,
    from: q.fromUs || undefined,
    to: q.toUs || undefined,
    limit: q.limit,
    cursor: q.cursor || undefined,
  };
  const res = await apiFetch<{ logs?: AuditLog[]; next_cursor?: string }>('/v3/admin/audit-logs', { query });
  return {
    logs: Array.isArray(res.data?.logs) ? res.data.logs : [],
    nextCursor: normalizeCursor(res.data?.next_cursor),
  };
}
